import * as B from '@blueprintjs/core';
import * as S from '@blueprintjs/select';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import React from 'react';

import {
  ApiFeatureCollection,
  ApiFeatureCollectionOverlayMetadataView,
  GeometryOverlaySetting,
  PropertyColorRule,
} from 'app/modules/Remote/FeatureCollection';
import SwatchColorPicker, {DEFAULT_COLORS} from 'app/pages/MonitorProjectView/SwatchColorPicker';
import {VIEW_TYPE_OVERLAY_METADATA} from 'app/utils/constants';
import {filterStyleableAttributes} from 'app/utils/overlayUtils';

import cs from './OverlaySettingsDialog.module.scss';
import {StyleableAttribute, useStyleableAttributes} from './useStyleableAttributes';

interface Props {
  setting: GeometryOverlaySetting;
  /** The full FC the dialog is editing. Drives styleable-attribute fetch + gating. */
  featureCollection: ApiFeatureCollection;
  onSave: (next: GeometryOverlaySetting) => Promise<void> | void;
  /** Drops this overlay's per-portfolio saved entry — reverts to system defaults. */
  onDelete?: () => Promise<void> | void;
  onClose: () => void;
  geometryType: GeometryOverlaySetting['geometryType'];
}

const FormRow: React.FunctionComponent<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({title, description, children}) => (
  <div className={cs.formRow}>
    <div className={cs.rowLabel}>
      <span className={cs.rowTitle}>{title}</span>
      <small className={cs.rowDescription}>{description}</small>
    </div>
    <div className={cs.rowControl}>{children}</div>
  </div>
);

const OverlaySettingsDialog: React.FunctionComponent<Props> = ({
  setting,
  featureCollection,
  onSave,
  onDelete,
  onClose,
  geometryType,
}) => {
  // Show Clear when the saved per-portfolio state differs from what the system
  // would render on its own. Both null + non-null mismatches qualify; isEqual(null, null)
  // = true so untouched (or saved-single matching a null system) hides Clear.
  const systemRule =
    featureCollection.views?.find(
      (v): v is ApiFeatureCollectionOverlayMetadataView =>
        v.type === VIEW_TYPE_OVERLAY_METADATA
    )?.default_property_color_rule ?? null;
  const hasUserCustomization = !isEqual(setting.propertyColorRule, systemRule);
  const isPolygon = geometryType === 'Polygon';
  const geometryLabel = isPolygon ? 'polygons' : geometryType === 'Line' ? 'lines' : 'points';
  const baseColorPalette = DEFAULT_COLORS.slice(0, 8);

  const {data: rawAttributes = [], isLoading: isLoadingAttributes} = useStyleableAttributes(
    featureCollection.id
  );
  const availableAttributes = React.useMemo(
    () => filterStyleableAttributes(featureCollection, rawAttributes),
    [featureCollection, rawAttributes]
  );
  const hasAttributes = availableAttributes.length > 0;

  const [alwaysShow, setAlwaysShow] = React.useState(setting.defaultEnabled);
  const [unfilled, setUnfilled] = React.useState(!!setting.defaultUnfilled);
  const [color, setColor] = React.useState(setting.color);
  const [mode, setMode] = React.useState<'single' | 'attribute'>(() =>
    setting.propertyColorRule ? 'attribute' : 'single'
  );
  // Holds the user's explicit attribute pick. Otherwise we derive the active
  // attribute from the saved rule (if still present) or fall back to the first.
  const [pickedAttributeName, setPickedAttributeName] = React.useState<string | null>(null);
  // Per-attribute color overrides; preserves user picks across attribute switches.
  const [colorsByAttribute, setColorsByAttribute] = React.useState<
    Record<string, Record<string, string>>
  >(() =>
    setting.propertyColorRule
      ? {[setting.propertyColorRule.propertyKey]: setting.propertyColorRule.colors}
      : {}
  );
  const [isSaving, setIsSaving] = React.useState(false);

  // Initial state derived from setting; used to detect and revert unsaved edits.
  const initialMode: 'single' | 'attribute' = setting.propertyColorRule ? 'attribute' : 'single';
  const initialColorsByAttribute = setting.propertyColorRule
    ? {[setting.propertyColorRule.propertyKey]: setting.propertyColorRule.colors}
    : {};
  const hasUnsavedChanges =
    alwaysShow !== setting.defaultEnabled ||
    unfilled !== !!setting.defaultUnfilled ||
    color !== setting.color ||
    mode !== initialMode ||
    pickedAttributeName !== null ||
    !isEqual(colorsByAttribute, initialColorsByAttribute);

  const handleResetEdits = () => {
    setAlwaysShow(setting.defaultEnabled);
    setUnfilled(!!setting.defaultUnfilled);
    setColor(setting.color);
    setMode(initialMode);
    setPickedAttributeName(null);
    setColorsByAttribute(initialColorsByAttribute);
  };

  const findAttr = (name: string | null | undefined) =>
    name ? availableAttributes.find((a) => a.name === name) : undefined;
  const selectedAttribute =
    findAttr(pickedAttributeName) ??
    findAttr(setting.propertyColorRule?.propertyKey) ??
    availableAttributes[0];
  const attributeName = selectedAttribute?.name ?? '';

  const overrides = selectedAttribute ? colorsByAttribute[selectedAttribute.name] ?? {} : {};
  const rows = selectedAttribute
    ? [...selectedAttribute.values].sort().map((value, i) => ({
        value,
        color: overrides[value] ?? DEFAULT_COLORS[i % DEFAULT_COLORS.length],
      }))
    : [];

  const setRowColor = (value: string, c: string) =>
    setColorsByAttribute((prev) => ({
      ...prev,
      [attributeName]: {...(prev[attributeName] ?? {}), [value]: c},
    }));

  const saveWith = async (rule: PropertyColorRule | null) => {
    const next: GeometryOverlaySetting = {
      ...setting,
      defaultEnabled: alwaysShow,
      color,
      defaultFilled: !unfilled,
      defaultUnfilled: unfilled,
      propertyColorRule: rule,
    };
    setIsSaving(true);
    try {
      await onSave(next);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = () => {
    if (mode === 'attribute' && selectedAttribute && rows.length > 0) {
      const rule: PropertyColorRule = {
        propertyKey: selectedAttribute.name,
        colors: Object.fromEntries(rows.map((r) => [r.value, r.color])),
      };
      return saveWith(rule);
    }
    // Single-color save: user keeps their color, no rule applied.
    return saveWith(null);
  };

  // Clear: drop the per-portfolio entry entirely; system defaults re-apply.
  const handleClearRule = async () => {
    if (!onDelete) return;
    setIsSaving(true);
    try {
      await onDelete();
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const colorModeButtons = (
    <B.ButtonGroup>
      <B.Button active={mode === 'single'} onClick={() => setMode('single')}>
        Single
      </B.Button>
      <B.Tooltip
        content={
          isLoadingAttributes
            ? 'Loading available attributes…'
            : "No attributes available for styling."
        }
        disabled={hasAttributes}
        position="left"
      >
        <B.Button
          active={mode === 'attribute'}
          disabled={!hasAttributes}
          onClick={() => setMode('attribute')}
        >
          By attribute
        </B.Button>
      </B.Tooltip>
    </B.ButtonGroup>
  );

  return (
    <B.Dialog
      isOpen
      onClose={onClose}
      title={setting.name}
      canOutsideClickClose={!isSaving}
      canEscapeKeyClose={!isSaving}
    >
      <B.DialogBody className={cs.body}>
        <FormRow title="Visible by default" description="Overlay will be visible when the page loads">
          <B.Switch
            large
            checked={alwaysShow}
            onChange={() => setAlwaysShow((v) => !v)}
            aria-label="Visible by default"
          />
        </FormRow>

        {isPolygon && (
          <FormRow title="Fill polygons" description="If selected, overlay will be filled instead of just an outline">
            <B.Switch
              large
              checked={!unfilled}
              onChange={() => setUnfilled((v) => !v)}
              aria-label="Fill polygons"
            />
          </FormRow>
        )}

        <FormRow
          title="Color mode"
          description="Choose a single color or vary colors by an attribute"
        >
          {colorModeButtons}
        </FormRow>

        {mode === 'single' && (
          <FormRow title="Color" description={`The color used for all ${geometryLabel}`}>
            <div className={cs.swatchGrid} role="radiogroup" aria-label="Color">
              {(baseColorPalette.includes(color)
                ? baseColorPalette
                : [...baseColorPalette, color]
              ).map((c) => (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={c === color}
                  aria-label={c}
                  className={classnames(cs.swatch, {[cs.swatchSelected]: c === color})}
                  style={{backgroundColor: c}}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
          </FormRow>
        )}

        {mode === 'attribute' && hasAttributes && (
          <>
            <FormRow title="Attribute" description="Specify a color for values on the attribute">
              <S.Select<StyleableAttribute>
                items={availableAttributes}
                filterable={false}
                itemRenderer={(a, {handleClick, modifiers}) => (
                  <B.MenuItem
                    key={a.name}
                    active={modifiers.active}
                    onClick={handleClick}
                    text={a.name}
                  />
                )}
                onItemSelect={(a) => setPickedAttributeName(a.name)}
                popoverProps={{usePortal: false, minimal: true}}
              >
                <B.Button
                  text={attributeName || 'Select attribute…'}
                  rightIcon="caret-down"
                  alignText="left"
                />
              </S.Select>
            </FormRow>

            {rows.length > 0 ? (
              <div className={cs.valueList}>
                {rows.map((row) => (
                  <div key={row.value} className={cs.valueRow}>
                    <SwatchColorPicker
                      color={row.color}
                      setColor={(c) => setRowColor(row.value, c)}
                      size={20}
                      closeOnSelect
                    />
                    <span className={cs.valueLabel}>{row.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <B.Callout intent="none" className={cs.emptyCallout}>
                No values are available for this attribute.
              </B.Callout>
            )}
          </>
        )}
      </B.DialogBody>

      <B.DialogFooter
        actions={
          <>
            {hasUserCustomization && (
              <B.Button
                intent="danger"
                minimal
                text="Reset to default"
                disabled={isSaving}
                onClick={handleClearRule}
              />
            )}
            {hasUnsavedChanges && (
              <B.Button
                minimal
                text="Discard changes"
                disabled={isSaving}
                onClick={handleResetEdits}
              />
            )}
            <B.Button text="Cancel" disabled={isSaving} onClick={onClose} />
            <B.Button
              intent="primary"
              text="Save"
              loading={isSaving}
              onClick={handleSave}
            />
          </>
        }
      >
        <small className={cs.scopeNote}>Changes apply to this portfolio.</small>
      </B.DialogFooter>
    </B.Dialog>
  );
};

export default OverlaySettingsDialog;
